import React, { useState } from "react";
import { captureMessage } from "@sentry/browser";
import { useHistory, useLocation } from "react-router";
import { buildLink } from "common/routing";
import {
  AiAction,
  useRunSubmissionAiActionMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { getCurrentURL } from "../../utils/url";
import { CommonModal } from "../Common/CommonModal";
import SelectionCardGroup from "../Inputs/SelectionCardGroup";

export type RunAIActionProps = {
  submission: {
    id: string;
    aiActions?: Array<AiAction>;
  };
  prevLocation?: string;
  closeModal: () => void;
};

const AIActionList = ({
  aiActions,
  setSelectedAIAction,
}: {
  aiActions: Array<AiAction>;
  setSelectedAIAction: (aiAction: Maybe<AiAction>) => void;
}) => {
  return (
    <SelectionCardGroup
      orientation="vertical"
      options={aiActions.map(aiAction => ({
        id: aiAction.id,
        title: aiAction.name,
        description: aiAction.description,
        value: aiAction.id,
      }))}
      onChange={value =>
        setSelectedAIAction(
          aiActions.find(aiAction => aiAction.id === value) ?? null
        )
      }
    />
  );
};

const RunAIAction = ({ submission, closeModal }: RunAIActionProps) => {
  const { addErrorToast } = useStatusToasts();
  const location = useLocation();

  const history = useHistory();

  const [selectedAIAction, setSelectedAIAction] =
    useState<Maybe<AiAction>>(null);

  const [runAiAction, { loading: runAiActionLoading }] =
    useRunSubmissionAiActionMutation({
      variables: {
        data: {
          submissionId: submission.id,
          aiActionId: selectedAIAction?.id!,
        },
      },
      onCompleted: data => {
        const id = data.runSubmissionAIAction;
        history.push(
          buildLink("aIActionRun", {
            id,
          }),
          {
            prevLocation: getCurrentURL(location),
          }
        );
      },
      onError: error => {
        captureMessage(error.message);
        closeModal();
        addErrorToast(
          "There was an issue running the AI action. Please try again. If the problem persists, please email us at support@withforerunner.com"
        );
      },
    });

  return (
    <CommonModal
      title={"AI Actions"}
      subtitle={"Choose the AI Action you'd like to run"}
      content={
        <AIActionList
          aiActions={submission.aiActions ?? []}
          setSelectedAIAction={setSelectedAIAction}
        />
      }
      onCancel={closeModal}
      disabled={!selectedAIAction}
      onSubmit={async () => {
        await runAiAction({
          variables: {
            data: {
              submissionId: submission.id,
              aiActionId: selectedAIAction!.id,
            },
          },
        });
      }}
      primaryButtonText={"Next"}
      loading={runAiActionLoading}
    />
  );
};

export default RunAIAction;
